import type { Address } from "viem"
import { useState } from "react"
import { useParams } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { useConfig } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { InnerCard } from "@/components/inner-card"
import { InnerCardWarning } from "@/components/inner-card-warning"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { ClFarmStore } from "@/domains/cl-farm"
import { AlmProviderImageLink } from "@/domains/cl-farms-page/components/alm-provider-image-link"
import { ClFarmsPageLayout } from "@/domains/cl-farms-page/components/cl-farms-page-layout"
import { ClFarmsSubHeader } from "@/domains/cl-farms-page/components/cl-farms-sub-header"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { DollarIcon, WalletIcon } from "@/domains/icons"
import { unstakeClFarmLiquidityInteraction } from "@/domains/interactions"
import { dollars } from "@/domains/library"
import { ClFarmsPagesComplexBreadcrumbApplication } from "@/domains/navigation/components/complex-breadcrumb-applications/cl-farms-pages-complex-breadcrumb-application"
import { ClFarmsLiquiditySubItem } from "@/domains/navigation/subitems/clfarms-liquidity-subitem"
import { ITokenInputBalance, TokenInputBalance } from "@/domains/tokens/components/token-input-balance"
import { TokenInputValue } from "@/domains/tokens/components/token-input-value"
import { ITokenPercentageButtons, TokenPercentageButtons } from "@/domains/tokens/components/token-percentage-buttons"
import { INSUFFICIENT_BALANCE_ERROR } from "@/features/liquidity/constants"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useClFarm } from "@/hooks/use-cl-farm"
import useEventEmitter from "@/hooks/useEventEmitter"

const BREADCRUMB_ITEMS = [new ClFarmsLiquiditySubItem(), ClFarmsPagesComplexBreadcrumbApplication]

const UnstakeClFarmPage = observer(() => {
  const { address: clFarmAddressParam } = useParams<{ address: string }>()
  const config = useConfig()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()
  const scaleApp = useScaleApplication()

  const clFarmAddress = clFarmAddressParam ? (clFarmAddressParam as Address) : undefined

  const clFarm = useClFarm(clFarmAddress)
  const [amount, setAmount] = useState<string>("0")
  const amountBI = clFarm?.parseUnits(amount) ?? 0n

  if (!clFarm) {
    return null
  }

  const unstakeAction = async () => {
    const chainId = chainConfig.chainId

    if (!scaleApp.Wallet.Connection.isConnected) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }
    if (!scaleApp.Wallet.Connection.connectedAddress) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }

    await unstakeClFarmLiquidityInteraction({
      connectedAddress: scaleApp.Wallet.Connection.connectedAddress.toViemAddress(),
      chainId,
      config,
      emitter,
      amount: amountBI,
      clFarm,
      onSuccess: () => {
        setAmount("0")
        scaleApp.ClFarms.Orchestrator.userInfoQueryInstance?.forceExecute()

        // the token should update it's balance
        scaleApp.Tokens.Orchestrator.balanceQuery?.forceExecute()
      },
    })
  }

  const title = clFarm ? `Unstake ${clFarm.displayName} CL Farms Liquidity` : "Unstake CL Farms Liquidity"
  const subTitle = "from the gauge to no longer receive rewards"

  // window.clFarm = clFarm
  return (
    <TooltipProvider>
      <PageContainer>
        <MetaData title={title} />
        <PageTitle title="Unstake CL Farms Liquidity" subTitle={subTitle} items={BREADCRUMB_ITEMS} />
        <ClFarmsPageLayout
          clFarmAddress={clFarmAddress}
          clFarm={clFarm}
          leftColumn={
            <UnstakeClFarm clFarm={clFarm} amount={amount} setAmount={setAmount} unstakeAction={unstakeAction} />
          }
        />
      </PageContainer>
    </TooltipProvider>
  )
})

export default UnstakeClFarmPage

type UnstakePairProps = {
  clFarm: ClFarmStore | null | undefined
  amount: string
  setAmount: (v: string) => void
  unstakeAction: () => void
}

const UnstakeClFarm = observer(({ clFarm, amount, setAmount, unstakeAction }: UnstakePairProps) => {
  const amountBI = clFarm?.parseUnits(amount) ?? 0n
  const hasSufficientBalance = clFarm ? amountBI <= clFarm.gaugeUserBalance : false
  const unstakeAmountUsd = clFarm ? clFarm?.calculateValueUSD(Number(amount)) : 0
  const canInteract = amountBI > 0n && hasSufficientBalance

  if (!clFarm) {
    return null
  }

  const buttonsToken: ITokenPercentageButtons & ITokenInputBalance = {
    address: clFarm.address,
    symbol: clFarm.symbol ?? "unknown",
    imageURL: clFarm.imageURL ?? "",
    decimals: clFarm.decimals ?? 18,
    displayBalance: clFarm.formatUnits(clFarm.onChainUserGaugeBalance ?? 0n),
    calculateValue: (percentage: number): string => {
      return clFarm?.calculateValuePercentage(clFarm.onChainUserGaugeBalance, percentage) ?? "0"
    },
  }

  return (
    <Card.Container className="flex w-full min-w-[320px]  flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 lg:p-6">
      <ShowWhenTrue when={!!clFarm}>
        <ClFarmsSubHeader
          displayName={clFarm?.displayName ?? ""}
          tags={clFarm?.tags ?? []}
          description={clFarm?.description ?? ""}
        />
        <InnerCard hasWarning={!hasSufficientBalance}>
          <div className="flex flex-row items-center justify-between">
            <TokenInputValue
              value={amount}
              token={buttonsToken}
              onValueChange={(v) => setAmount(v)}
              className="h-fit min-w-[180px] flex-grow p-0"
              placeholder="0"
            />
            {/* <TokenInput.Value
              value={amount}
              decimals={clFarm?.decimals ?? 18}
              symbol={clFarm?.symbol ?? "unknown"}
              onValueChange={(v) => setAmount(v)}
              className="h-fit min-w-[180px] flex-grow p-0"
              placeholder="0"
            /> */}
          </div>

          {/* balances */}
          <div className="flex w-full flex-row items-center justify-between text-xs">
            <ShowWhenTrue when={hasSufficientBalance}>
              <div className="flex items-center gap-1 text-sm text-t200">
                <DollarIcon size={14} className="text-t200" />
                <p>~{dollars(unstakeAmountUsd)}</p>
              </div>
            </ShowWhenTrue>
            <ShowWhenTrue when={!hasSufficientBalance}>
              <InnerCardWarning warning={INSUFFICIENT_BALANCE_ERROR} />
            </ShowWhenTrue>
            <Tooltip key={"balance"}>
              <TooltipTrigger asChild>
                <TokenInputBalance token={buttonsToken} label={<WalletIcon size={14} className="text-t200" />} />
              </TooltipTrigger>
              <TooltipContent>
                <div>{clFarm?.formatUnits(clFarm?.onChainUserGaugeBalance ?? 0n)}</div>
              </TooltipContent>
            </Tooltip>
          </div>

          <TokenPercentageButtons token={buttonsToken} onClick={setAmount} />
        </InnerCard>

        <div className="my-2 flex w-full items-center justify-between">
          <AlmProviderImageLink clFarm={clFarm} />
          <Button disabled={!canInteract} variant={"primary"} onClick={unstakeAction} size="fit">
            Unstake
          </Button>
        </div>
      </ShowWhenTrue>
    </Card.Container>
  )
})
